import { X } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils/cn';
import type { LinkPreview } from '@/lib/api/links';

type Props = {
  preview?: LinkPreview;
  onDismiss?: () => void;
  isLoading?: boolean;
};

export function LinkPreviewCard({ preview, onDismiss, isLoading }: Props) {
  if (isLoading) {
    return (
      <div className="relative border border-muted/30 rounded-lg overflow-hidden bg-cream-warm/40 p-3 space-y-2">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
      </div>
    );
  }

  if (!preview) return null;

  const isVideo = preview.type === 'video' && preview.embed_url;

  let domain = '';
  try { domain = new URL(preview.url).hostname.replace(/^www\./, ''); } catch { /* ignore */ }

  const wrapper = cn(
    'relative border border-muted/30 rounded-lg overflow-hidden bg-cream-warm/40',
  );

  return (
    <div className={wrapper}>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="absolute top-2 right-2 z-10 rounded-full bg-ink/60 p-0.5 text-cream hover:bg-ink/80 transition-colors"
          aria-label="Remove link preview"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      )}

      {isVideo ? (
        <div>
          <div className="relative pb-[56.25%]">
            <iframe
              src={preview.embed_url}
              className="absolute inset-0 w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title={preview.title ?? 'Video'}
            />
          </div>
          {(preview.title || domain) && (
            <div className="px-3 py-2">
              {preview.title && (
                <a
                  href={preview.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-navy hover:underline line-clamp-1"
                >
                  {preview.title}
                </a>
              )}
              {domain && <p className="text-xs text-muted mt-0.5">{domain}</p>}
            </div>
          )}
        </div>
      ) : (
        <a
          href={preview.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex gap-3 p-3 hover:bg-cream-warm/60 transition-colors"
        >
          {preview.image && (
            <img
              src={preview.image}
              alt=""
              className="shrink-0 w-16 h-16 object-cover rounded"
            />
          )}
          <div className="min-w-0 flex-1">
            {preview.title && (
              <p className="text-sm font-medium text-navy line-clamp-2">{preview.title}</p>
            )}
            {preview.description && (
              <p className="text-xs text-muted line-clamp-1 mt-0.5">{preview.description}</p>
            )}
            {domain && <p className="text-xs text-muted/70 mt-1">{domain}</p>}
          </div>
        </a>
      )}
    </div>
  );
}
